import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { searchNavigationByName } from './api';
import { mapNavigationResolveToDomain } from './mappers';
import type { NavigationMatch } from './types';

export const NAVIGATION_SEARCH_KEY = 'navigation-search';
const SEARCH_DEBOUNCE_MS = 200;

export interface UseSearchNavigationNamesResult {
    data: NavigationMatch[];
    isLoading: boolean;
    isFetching: boolean;
    isFetched: boolean;
    error: unknown;
}

function useDebouncedValue(value: string, delayMs: number): string {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const timeoutId = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(timeoutId);
    }, [value, delayMs]);

    return debounced;
}

export function useSearchNavigationNames(
    query: string,
    options?: { enabled?: boolean }
): UseSearchNavigationNamesResult {
    const trimmed = query.trim();
    const enabled = (options?.enabled ?? true) && trimmed.length > 0;
    const debouncedQuery = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
    const queryEnabled = enabled && debouncedQuery.length > 0 && debouncedQuery === trimmed;

    const searchQuery = useQuery({
        queryKey: [NAVIGATION_SEARCH_KEY, debouncedQuery],
        queryFn: () => searchNavigationByName(debouncedQuery),
        enabled: queryEnabled,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    const isDebouncing = enabled && debouncedQuery !== trimmed;
    const data = useMemo(() => {
        if (!enabled || debouncedQuery !== trimmed) {
            return [];
        }
        return mapNavigationResolveToDomain(searchQuery.data?.data);
    }, [enabled, debouncedQuery, trimmed, searchQuery.data?.data]);

    return {
        data,
        isLoading: searchQuery.isPending,
        isFetching: searchQuery.isFetching || isDebouncing,
        isFetched: searchQuery.isFetched,
        error: searchQuery.error,
    };
}
